import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  Clock,
  Library,
  PenLine,
  Target,
  X,
  Zap,
  type LucideIcon
} from 'lucide-react';
import { LearnerSlideModalShell } from './shared/LearnerSlideModalShell';
import { PersianText } from './shared/PersianText';
import {
  HomeProductDemoPreview,
  type HomeDemoPreviewKind
} from './HomeProductDemoPreview';
import { useLearnerSlideModal } from '../hooks/useLearnerSlideModal';
import { useAppLocale } from '../providers/LocaleProvider';
import { apiService } from '../services/api';
import { pickLocalized, pickLocalizedTitle } from '../utils/localizedContent';
import { localeMirroredNavArrowClass } from '../utils/localeNavArrow';
import { normalizeAppHostedUploadUrl } from '../utils/normalizeAppHostedUploadUrl';
import { prefersReducedMotion } from '../utils/subscriptionAccessibility';

export interface HomeProductDemoModalProps {
  open: boolean;
  onClose: () => void;
  /** Guest CTA — open register; signed-in users go to dashboard. */
  onStartLearning?: () => void;
  isSignedIn?: boolean;
}

type DemoStepId = 'lessons' | 'quiz' | 'exercise' | 'books' | 'xp';

interface HomeDemoLessonSample {
  id: number;
  title: string;
  titleFa?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  thumbnailUrl?: string | null;
  xpScore: number;
  courseId: number;
  courseTitle: string;
  courseTitleFa?: string | null;
  levelId: number;
  levelTitle: string;
  levelTitleFa?: string | null;
  sampleExcerpt?: string | null;
  sampleExcerptFa?: string | null;
  sampleBlockTitle?: string | null;
  sampleBlockTitleFa?: string | null;
  sampleHasAudio?: boolean;
}

interface HomeDemoPracticeSample {
  id: number;
  kind: 'quiz' | 'exercise' | string;
  title: string;
  titleFa?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  xpScore: number;
  questionCount: number;
  timeLimitMinutes?: number | null;
  passingScore?: number | null;
  lessonId: number;
  lessonTitle: string;
  lessonTitleFa?: string | null;
}

interface HomeDemoBookSample {
  id: number;
  title: string;
  farsiTitle?: string | null;
  author?: string | null;
  farsiAuthor?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  coverImageUrl?: string | null;
  difficulty: string;
  bookType: string;
  sampleExcerpt?: string | null;
  sampleBlockTitle?: string | null;
  sampleHasAudio?: boolean;
}

interface HomeDemoXpSample {
  lessonXp: number;
  quizXp: number;
  exerciseXp: number;
  exampleEarnTotal: number;
  offerName?: string | null;
  offerXpCost?: number | null;
  offerGrantDays?: number | null;
  offerPlanName?: string | null;
}

interface HomeProductDemoPayload {
  lesson?: HomeDemoLessonSample | null;
  quiz?: HomeDemoPracticeSample | null;
  exercise?: HomeDemoPracticeSample | null;
  book?: HomeDemoBookSample | null;
  xp?: HomeDemoXpSample | null;
}

const DEMO_STEPS: {
  id: DemoStepId;
  Icon: LucideIcon;
  iconWrapClass: string;
  accentBarClass: string;
}[] = [
  {
    id: 'lessons',
    Icon: BookOpen,
    iconWrapClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/55 dark:text-amber-200',
    accentBarClass: 'from-amber-500 to-amber-700'
  },
  {
    id: 'quiz',
    Icon: Target,
    iconWrapClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/55 dark:text-teal-200',
    accentBarClass: 'from-teal-500 to-emerald-600'
  },
  {
    id: 'exercise',
    Icon: PenLine,
    iconWrapClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/55 dark:text-emerald-200',
    accentBarClass: 'from-emerald-500 to-teal-600'
  },
  {
    id: 'books',
    Icon: Library,
    iconWrapClass: 'bg-sky-100 text-sky-800 dark:bg-sky-900/55 dark:text-sky-200',
    accentBarClass: 'from-sky-500 to-blue-600'
  },
  {
    id: 'xp',
    Icon: Zap,
    iconWrapClass: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200',
    accentBarClass: 'from-amber-500 to-orange-600'
  }
];

const PANEL_CLASS =
  'max-h-[min(94vh,720px)] w-full max-w-xl overflow-hidden border border-amber-200/80 bg-white dark:border-slate-600 dark:bg-slate-800';

function HowToList({ tips }: { tips: string[] }) {
  return (
    <ul className="mt-4 w-full space-y-2 text-start">
      {tips.map((tip) => (
        <li
          key={tip}
          className="flex items-start gap-2 rounded-xl bg-slate-50/90 px-3 py-2 text-xs leading-snug text-slate-700 dark:bg-slate-700/70 dark:text-slate-100 sm:text-sm"
        >
          <CheckCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300"
            strokeWidth={2.5}
            aria-hidden
          />
          <span>{tip}</span>
        </li>
      ))}
    </ul>
  );
}

function SampleMetaChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-slate-700 ring-1 ring-slate-200/80 dark:bg-slate-700/90 dark:text-slate-100 dark:ring-slate-500/40 sm:text-[11px]">
      {children}
    </span>
  );
}

function ContentExcerptPreview({
  label,
  title,
  excerpt,
  hasAudio,
  tone = 'amber'
}: {
  label: string;
  title?: string;
  excerpt: string;
  hasAudio?: boolean;
  tone?: 'amber' | 'sky';
}) {
  const toneClass =
    tone === 'sky'
      ? 'border-sky-200/80 bg-sky-50/90 dark:border-sky-600/50 dark:bg-sky-900/35'
      : 'border-amber-200/80 bg-amber-50/90 dark:border-amber-600/45 dark:bg-amber-900/30';

  return (
    <div className={`mt-2 rounded-xl border px-2.5 py-2 text-start ${toneClass}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
        {label}
      </p>
      {title ? (
        <p className="mt-0.5 text-xs font-semibold text-slate-800 dark:text-slate-100">
          <PersianText>{title}</PersianText>
        </p>
      ) : null}
      <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-slate-700 dark:text-slate-200">
        <PersianText>{excerpt}</PersianText>
      </p>
      {hasAudio ? (
        <div className="mt-2 flex h-7 items-center gap-2 rounded-lg bg-white/80 px-2 ring-1 ring-black/5 dark:bg-slate-800/80 dark:ring-white/10">
          <div className={`h-4 w-4 rounded-full ${tone === 'sky' ? 'bg-sky-500' : 'bg-amber-500'}`} />
          <div className="h-1 flex-1 rounded-full bg-slate-200 dark:bg-slate-600">
            <div className={`h-full w-2/5 rounded-full ${tone === 'sky' ? 'bg-sky-500' : 'bg-amber-500'}`} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Short product intro for the home-page "View Demo" CTA:
 * lessons → quizzes & exercises → books → earning XP.
 * Loads real first-catalog samples from `/api/Health/home-demo`.
 */
export const HomeProductDemoModal: React.FC<HomeProductDemoModalProps> = ({
  open,
  onClose,
  onStartLearning,
  isSignedIn = false
}) => {
  const { t } = useTranslation('home');
  const { locale, direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [stepIndex, setStepIndex] = useState(0);
  const [samples, setSamples] = useState<HomeProductDemoPayload | null>(null);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [samplesError, setSamplesError] = useState(false);
  const [previewKind, setPreviewKind] = useState<HomeDemoPreviewKind | null>(null);
  const [stepAnim, setStepAnim] = useState<'idle' | 'out' | 'prein'>('idle');
  const [stepDir, setStepDir] = useState<1 | -1>(1);
  const { mounted, visible, panelRef, requestClose, handleExitTransitionEnd } = useLearnerSlideModal(
    open,
    onClose,
    undefined,
    { historyBackId: 'home-product-demo' }
  );

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setPreviewKind(null);
      setStepAnim('idle');
      setStepDir(1);
    }
  }, [open]);

  const goToStep = (nextIndex: number) => {
    if (nextIndex === stepIndex || nextIndex < 0 || nextIndex >= DEMO_STEPS.length) return;
    const dir: 1 | -1 = nextIndex > stepIndex ? 1 : -1;
    setStepDir(dir);
    setPreviewKind(null);
    if (prefersReducedMotion()) {
      setStepIndex(nextIndex);
      setStepAnim('idle');
      return;
    }
    setStepAnim('out');
    window.setTimeout(() => {
      setStepIndex(nextIndex);
      setStepAnim('prein');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setStepAnim('idle'));
      });
    }, 180);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setSamplesLoading(true);
      setSamplesError(false);
      try {
        const data = await apiService.get<HomeProductDemoPayload>('/api/Health/home-demo', {
          cache: false
        });
        const payload = (data as { data?: HomeProductDemoPayload })?.data || data;
        if (!cancelled) setSamples(payload ?? null);
      } catch (error) {
        console.error('Error fetching home product demo samples:', error);
        if (!cancelled) {
          setSamples(null);
          setSamplesError(true);
        }
      } finally {
        if (!cancelled) setSamplesLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!mounted) return null;

  const total = DEMO_STEPS.length;
  const step = DEMO_STEPS[stepIndex]!;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex >= total - 1;
  const StepIcon = step.Icon;
  const howToTips = [1, 2, 3].map((n) => t(`demo.${step.id}.howTo${n}`));

  const goNext = () => {
    if (isLast) {
      requestClose();
      return;
    }
    goToStep(stepIndex + 1);
  };

  const goBack = () => {
    if (!isFirst) goToStep(stepIndex - 1);
  };

  const exitShift =
    stepDir > 0 ? (isRtl ? 'translate-x-5' : '-translate-x-5') : isRtl ? '-translate-x-5' : 'translate-x-5';
  const enterShift =
    stepDir > 0 ? (isRtl ? '-translate-x-5' : 'translate-x-5') : isRtl ? 'translate-x-5' : '-translate-x-5';
  const stepMotionClass =
    stepAnim === 'out'
      ? `opacity-0 ${exitShift} scale-[0.98]`
      : stepAnim === 'prein'
        ? `opacity-0 ${enterShift} scale-[0.98]`
        : 'opacity-100 translate-x-0 scale-100';

  const renderLessonSample = () => {
    const lesson = samples?.lesson;
    if (!lesson) return null;
    const title = pickLocalizedTitle(lesson, locale);
    const courseTitle = pickLocalizedTitle(
      { title: lesson.courseTitle, titleFa: lesson.courseTitleFa },
      locale
    );
    const levelTitle = pickLocalizedTitle(
      { title: lesson.levelTitle, titleFa: lesson.levelTitleFa },
      locale
    );
    const description = pickLocalized(lesson.description, lesson.descriptionFa, locale);
    const excerpt = pickLocalized(lesson.sampleExcerpt, lesson.sampleExcerptFa, locale);
    const excerptTitle = pickLocalized(lesson.sampleBlockTitle, lesson.sampleBlockTitleFa, locale);
    const thumb = lesson.thumbnailUrl ? normalizeAppHostedUploadUrl(lesson.thumbnailUrl) : null;

    return (
      <button
        type="button"
        onClick={() => setPreviewKind('lesson')}
        className="mt-4 w-full overflow-hidden rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-white text-start shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-600/45 dark:from-amber-900/40 dark:to-slate-800"
      >
        <div className="flex gap-3 p-3 sm:p-4">
          <div className="relative h-20 w-16 shrink-0 overflow-hidden rounded-xl bg-amber-100 dark:bg-amber-900/50 sm:h-24 sm:w-20">
            {thumb ? (
              <img src={thumb} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-amber-700 dark:text-amber-300">
                <BookOpen className="h-8 w-8" strokeWidth={2} aria-hidden />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800/80 dark:text-amber-300">
              {t('demo.sampleLesson')}
            </p>
            <h4 className="mt-0.5 text-base font-bold text-slate-900 dark:text-white sm:text-lg">
              <PersianText>{title}</PersianText>
            </h4>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
              {t('demo.fromPath', { course: courseTitle, level: levelTitle })}
            </p>
            {description ? (
              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <PersianText>{description}</PersianText>
              </p>
            ) : null}
            {excerpt ? (
              <ContentExcerptPreview
                label={t('demo.lessonExcerpt')}
                title={excerptTitle || undefined}
                excerpt={excerpt}
                hasAudio={!!lesson.sampleHasAudio}
                tone="amber"
              />
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {lesson.xpScore > 0 ? (
                <SampleMetaChip>
                  <Zap className="h-3 w-3 text-amber-600" aria-hidden />
                  {t('demo.xpPoints', { count: lesson.xpScore })}
                </SampleMetaChip>
              ) : null}
              <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                {t('demo.tapToExplore')}
              </span>
            </div>
          </div>
        </div>
      </button>
    );
  };

  const renderPracticeCard = (item: HomeDemoPracticeSample, kindLabel: string) => {
    const title = pickLocalizedTitle(item, locale);
    const lessonTitle = pickLocalizedTitle(
      { title: item.lessonTitle, titleFa: item.lessonTitleFa },
      locale
    );
    const description = pickLocalized(item.description, item.descriptionFa, locale);
    const isQuiz = item.kind === 'quiz';

    return (
      <button
        type="button"
        key={`${item.kind}-${item.id}`}
        onClick={() => setPreviewKind(isQuiz ? 'quiz' : 'exercise')}
        className="w-full overflow-hidden rounded-2xl border border-teal-200/80 bg-gradient-to-br from-teal-50 to-white text-start shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:border-teal-600/45 dark:from-teal-900/35 dark:to-slate-800"
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                isQuiz
                  ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/55 dark:text-teal-200'
                  : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/55 dark:text-emerald-200'
              }`}
            >
              <Target className="h-5 w-5" strokeWidth={2.1} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-teal-800/80 dark:text-teal-300/90">
                {kindLabel}
              </p>
              <h4 className="mt-0.5 text-sm font-bold text-slate-900 dark:text-white sm:text-base">
                <PersianText>{title}</PersianText>
              </h4>
              <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                {t('demo.inLesson', { lesson: lessonTitle })}
              </p>
            </div>
          </div>
          {description ? (
            <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
              <PersianText>{description}</PersianText>
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {item.questionCount > 0 ? (
              <SampleMetaChip>{t('demo.questionCount', { count: item.questionCount })}</SampleMetaChip>
            ) : null}
            {isQuiz && item.timeLimitMinutes ? (
              <SampleMetaChip>
                <Clock className="h-3 w-3" aria-hidden />
                {t('demo.timeLimit', { minutes: item.timeLimitMinutes })}
              </SampleMetaChip>
            ) : null}
            {isQuiz && item.passingScore ? (
              <SampleMetaChip>{t('demo.passingScore', { score: item.passingScore })}</SampleMetaChip>
            ) : null}
            {item.xpScore > 0 ? (
              <SampleMetaChip>
                <Zap className="h-3 w-3 text-amber-600" aria-hidden />
                {t('demo.xpPoints', { count: item.xpScore })}
              </SampleMetaChip>
            ) : null}
            <span className="text-[11px] font-semibold text-teal-700 dark:text-teal-300">
              {t('demo.tapToExplore')}
            </span>
          </div>
        </div>
      </button>
    );
  };

  const renderQuizSample = () => {
    const quiz = samples?.quiz;
    if (!quiz) return null;
    return <div className="mt-4 w-full">{renderPracticeCard(quiz, t('demo.sampleQuiz'))}</div>;
  };

  const renderExerciseSample = () => {
    const exercise = samples?.exercise;
    if (!exercise) return null;
    return (
      <div className="mt-4 w-full">{renderPracticeCard(exercise, t('demo.sampleExercise'))}</div>
    );
  };

  const renderBookSample = () => {
    const book = samples?.book;
    if (!book) return null;
    const title = pickLocalized(book.title, book.farsiTitle, locale);
    const author = pickLocalized(book.author, book.farsiAuthor, locale);
    const description = pickLocalized(book.description, book.descriptionFa, locale);
    const excerpt = book.sampleExcerpt?.trim() || '';
    const excerptTitle = book.sampleBlockTitle?.trim() || '';
    const cover = book.coverImageUrl ? normalizeAppHostedUploadUrl(book.coverImageUrl) : null;
    const difficultyKey = `demo.difficulty${book.difficulty}` as const;
    const typeKey = `demo.bookType${book.bookType}` as const;

    return (
      <button
        type="button"
        onClick={() => setPreviewKind('book')}
        className="mt-4 w-full overflow-hidden rounded-2xl border border-sky-200/80 bg-gradient-to-br from-sky-50 to-white text-start shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-sky-600/45 dark:from-sky-900/35 dark:to-slate-800"
      >
        <div className="flex gap-3 p-3 sm:p-4">
          <div className="relative h-24 w-[4.25rem] shrink-0 overflow-hidden rounded-lg bg-sky-100 shadow-inner dark:bg-sky-900/50 sm:h-28 sm:w-20">
            {cover ? (
              <img src={cover} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sky-700 dark:text-sky-300">
                <Library className="h-8 w-8" strokeWidth={2} aria-hidden />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800/80 dark:text-sky-300">
              {t('demo.sampleBook')}
            </p>
            <h4 className="mt-0.5 text-base font-bold text-slate-900 dark:text-white sm:text-lg">
              <PersianText>{title}</PersianText>
            </h4>
            {author ? (
              <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                <PersianText>{author}</PersianText>
              </p>
            ) : null}
            {description ? (
              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                <PersianText>{description}</PersianText>
              </p>
            ) : null}
            {excerpt ? (
              <ContentExcerptPreview
                label={t('demo.bookExcerpt')}
                title={excerptTitle || undefined}
                excerpt={excerpt}
                hasAudio={!!book.sampleHasAudio}
                tone="sky"
              />
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <SampleMetaChip>{t(difficultyKey, { defaultValue: book.difficulty })}</SampleMetaChip>
              <SampleMetaChip>{t(typeKey, { defaultValue: book.bookType })}</SampleMetaChip>
              <span className="text-[11px] font-semibold text-sky-700 dark:text-sky-300">
                {t('demo.tapToExplore')}
              </span>
            </div>
          </div>
        </div>
      </button>
    );
  };

  const renderXpSample = () => {
    const xp = samples?.xp;
    if (!xp) return null;
    const rows = [
      { key: 'lesson', label: t('demo.xpFromLesson'), value: xp.lessonXp },
      { key: 'quiz', label: t('demo.xpFromQuiz'), value: xp.quizXp },
      { key: 'exercise', label: t('demo.xpFromExercise'), value: xp.exerciseXp }
    ].filter((r) => r.value > 0);

    return (
      <div className="mt-4 w-full space-y-3 text-start">
        <article className="overflow-hidden rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-orange-50/60 p-3 shadow-sm dark:border-amber-600/45 dark:from-amber-900/40 dark:to-orange-900/25 sm:p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800/80 dark:text-amber-300">
            {t('demo.sampleXpEarn')}
          </p>
          {rows.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {rows.map((row) => (
                <li
                  key={row.key}
                  className="flex items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-100"
                >
                  <span>{row.label}</span>
                  <span className="font-bold tabular-nums text-amber-800 dark:text-amber-300">
                    {t('demo.xpPoints', { count: row.value })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{t('demo.xpEarnGeneric')}</p>
          )}
          {xp.exampleEarnTotal > 0 ? (
            <p className="mt-3 border-t border-amber-200/70 pt-2 text-sm font-semibold text-slate-900 dark:border-amber-600/40 dark:text-white">
              {t('demo.xpExampleTotal', { count: xp.exampleEarnTotal })}
            </p>
          ) : null}
        </article>

        {xp.offerName && xp.offerXpCost ? (
          <article className="overflow-hidden rounded-2xl border border-violet-200/70 bg-gradient-to-br from-violet-50 to-white p-3 shadow-sm dark:border-violet-600/45 dark:from-violet-900/35 dark:to-slate-800 sm:p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-violet-800/80 dark:text-violet-300">
              {t('demo.sampleXpOffer')}
            </p>
            <h4 className="mt-1 text-sm font-bold text-slate-900 dark:text-white sm:text-base">
              {xp.offerName}
            </h4>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
              {t('demo.xpOfferDetail', {
                cost: xp.offerXpCost,
                days: xp.offerGrantDays ?? 0,
                plan: xp.offerPlanName ?? ''
              })}
            </p>
          </article>
        ) : null}
      </div>
    );
  };

  const renderSampleArea = () => {
    if (samplesLoading) {
      return (
        <div className="mt-4 flex w-full items-center justify-center gap-2 py-6 text-sm text-slate-500 dark:text-slate-400">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
          {t('demo.loadingSamples')}
        </div>
      );
    }

    if (samplesError) {
      return (
        <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
          {t('demo.samplesUnavailable')}
        </p>
      );
    }

    switch (step.id) {
      case 'lessons':
        return renderLessonSample() ?? (
          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">{t('demo.noLessonSample')}</p>
        );
      case 'quiz':
        return renderQuizSample() ?? (
          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">{t('demo.noQuizSample')}</p>
        );
      case 'exercise':
        return renderExerciseSample() ?? (
          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">{t('demo.noExerciseSample')}</p>
        );
      case 'books':
        return renderBookSample() ?? (
          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">{t('demo.noBookSample')}</p>
        );
      case 'xp':
        return renderXpSample();
      default:
        return null;
    }
  };

  const shell = (
    <LearnerSlideModalShell
      mounted={mounted}
      visible={visible}
      panelRef={panelRef}
      onBackdropClick={requestClose}
      onPanelTransitionEnd={handleExitTransitionEnd}
      panelClassName={PANEL_CLASS}
      ariaLabelledBy="home-product-demo-title"
      align="center"
      zIndexClass="z-[120]"
    >
      <div className="flex max-h-[min(94vh,720px)] flex-col">
        {/* Sticky header: brand title + step progress */}
        <header className="shrink-0 border-b border-amber-100/70 bg-gradient-to-b from-amber-50/80 via-[#fff8ef]/70 to-white dark:border-slate-600/80 dark:from-amber-950/25 dark:via-slate-800/80 dark:to-slate-800">
          <div className={`h-1.5 w-full bg-gradient-to-r ${step.accentBarClass}`} aria-hidden />

          <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-4 sm:px-6 sm:pt-5">
            <div className="min-w-0 text-start">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-800/80 dark:text-amber-300">
                {t('demo.title')}
              </p>
              <h2
                id="home-product-demo-title"
                className="mt-1 text-lg font-bold tracking-tight text-slate-900 dark:text-white sm:text-xl"
              >
                {t('demo.subtitle')}
              </h2>
            </div>
            <button
              type="button"
              onClick={requestClose}
              className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-white/70 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/80 dark:hover:text-white"
              aria-label={t('demo.closeAria')}
            >
              <X className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            </button>
          </div>

          <div className="px-5 pb-4 sm:px-6">
            <div
              className="flex items-center gap-1.5"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={total}
              aria-valuenow={stepIndex + 1}
            >
              {DEMO_STEPS.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => goToStep(i)}
                  className={`h-1.5 flex-1 rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                    i === stepIndex
                      ? 'bg-amber-600 dark:bg-amber-400'
                      : i < stepIndex
                        ? 'bg-amber-500/70 dark:bg-amber-500/60'
                        : 'bg-slate-200/90 dark:bg-slate-600'
                  }`}
                  aria-label={t('demo.stepOf', { current: i + 1, total })}
                  aria-current={i === stepIndex ? 'step' : undefined}
                />
              ))}
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-3">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {t('demo.stepOf', { current: stepIndex + 1, total })}
              </p>
              <p className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
                {t(`demo.${step.id}.title`)}
              </p>
            </div>
          </div>
        </header>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          <div
            key={step.id}
            className={`flex flex-col items-center text-center transition-[opacity,transform] duration-300 ease-out ${stepMotionClass}`}
          >
            <div
              className={`mb-3 flex h-14 w-14 items-center justify-center rounded-2xl shadow-inner ring-1 ring-black/5 dark:ring-white/10 ${step.iconWrapClass}`}
            >
              <StepIcon className="h-7 w-7" strokeWidth={2.1} aria-hidden />
            </div>
            <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">
              {t(`demo.${step.id}.title`)}
            </h3>
            <p className="mt-1.5 max-w-md text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              {t(`demo.${step.id}.body`)}
            </p>

            {renderSampleArea()}
            <HowToList tips={howToTips} />
          </div>
        </div>

        {/* Sticky footer: navigation actions */}
        <footer className="shrink-0 border-t border-amber-100/70 bg-gradient-to-t from-amber-50/75 via-[#fff8ef]/65 to-white px-5 py-3.5 dark:border-slate-600/80 dark:from-amber-950/22 dark:via-slate-800/75 dark:to-slate-800 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={isFirst}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-white/70 disabled:pointer-events-none disabled:opacity-40 dark:text-slate-200 dark:hover:bg-slate-700/80"
            >
              {t('demo.back')}
            </button>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isLast && onStartLearning ? (
                <button
                  type="button"
                  onClick={() => {
                    requestClose();
                    onStartLearning();
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:from-amber-700 hover:to-amber-800 dark:from-amber-500 dark:to-amber-600 sm:px-5"
                >
                  {isSignedIn ? t('hero.ctaContinue') : t('demo.ctaStart')}
                  <ArrowRight
                    className={localeMirroredNavArrowClass(isRtl, 'h-4 w-4 shrink-0')}
                    aria-hidden
                  />
                </button>
              ) : null}
              <button
                type="button"
                onClick={goNext}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all sm:px-5 ${
                  isLast
                    ? 'border border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600'
                    : 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md hover:from-amber-700 hover:to-amber-800 dark:from-amber-500 dark:to-amber-600'
                }`}
              >
                {isLast ? t('demo.done') : t('demo.next')}
                {!isLast ? (
                  <ArrowRight
                    className={localeMirroredNavArrowClass(isRtl, 'h-4 w-4 shrink-0')}
                    aria-hidden
                  />
                ) : null}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </LearnerSlideModalShell>
  );

  return (
    <>
      {createPortal(shell, document.body)}
      {previewKind ? (
        <HomeProductDemoPreview
          open={!!previewKind}
          kind={previewKind}
          onClose={() => setPreviewKind(null)}
          lesson={samples?.lesson ?? null}
          practice={
            previewKind === 'quiz'
              ? samples?.quiz ?? null
              : previewKind === 'exercise'
                ? samples?.exercise ?? null
                : null
          }
          book={samples?.book ?? null}
        />
      ) : null}
    </>
  );
};

export default HomeProductDemoModal;
